/**
 * URL Builder Utilities
 * Helpers for building SEO-friendly URLs and canonical URLs
 */

import slugify from '@sindresorhus/slugify';
import { SITE_CONFIG, LANG_TO_COUNTRY } from './config';
import { JOBALTO_URL, PROFITROLL_URL_LANGUAGES } from 'variables/main';

// Profession & skill data is delivered already localized: services/api
// stamps every request with `X-API-Language: <currentLocale>` and the
// backend returns a single localized `name` string. So slug helpers don't
// need to know about locale — they just slugify whatever name came back.
//
// Never returns undefined to avoid /professions/undefined/:id.
export const getProfessionSlug = (profession) => {
  if (!profession) return 'profession';
  return profession.slug || slugify(profession.name || '') || 'profession';
};

export const getSkillSlug = (skill) => {
  if (!skill) return 'skill';
  return skill.slug || slugify(skill.name || '') || 'skill';
};

// Unlike profession/skill slugs, the backend `slug` on vacancies is often the
// raw, unslugified title (contains spaces and '/', e.g. "Агент/-ка"), which
// produces malformed URLs that crash Safari navigation. Always derive from the
// title so links, canonical, and JSON-LD agree on one clean slug.
export const getVacancySlug = (vacancy) => {
  if (!vacancy) return 'vacancy';
  return slugify(vacancy.title || vacancy.name || '') || 'vacancy';
};

/**
 * Get the base URL for the current environment
 * For localized paths (e.g., /en-gb/), returns jobalto.com
 * For non-localized paths (e.g., /professions/), returns profitroll.com.ua
 *
 * Path-first because the prerender service navigates puppeteer to an internal
 * `http://frontend` host. Reading window.location.origin in that context
 * would emit canonical/OG URLs pointing at `http://frontend/...`.
 */
export const getBaseUrl = (path = null) => {
  const currentPath = path || (typeof window !== 'undefined' ? window.location.pathname : '');

  // Locale-prefixed paths (e.g. /en-gb/, /de-de/) live on jobalto.com.
  if (/^\/[a-z]{2}-[a-z]{2}(\/|$)/i.test(currentPath)) {
    return `https://${JOBALTO_URL}`;
  }

  // Trust the real origin only when it's a public production host. Anything
  // else (localhost, internal Docker hostnames like http://frontend, IPs) falls
  // back to the canonical Profitroll URL.
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.includes('jobalto.com')) {
      return origin;
    }
    if (origin.includes('profitroll.com.ua')) {
      return origin;
    }
  }
  return SITE_CONFIG.url;
};

/**
 * Build canonical URL for a given path
 */
export const buildCanonicalUrl = (path = '') => {
  const baseUrl = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Build URL for a specific entity (profession, article, etc.)
 *
 * Locale handling: if a `locale` is passed, use it verbatim. Otherwise derive
 * the locale prefix from `window.location.pathname` so the canonical of e.g.
 * `/de-de/professions/uebersetzer/<id>` stays `/de-de/...` instead of
 * collapsing to the locale-less `/professions/...` form (which would tell
 * Google every localized variant is a duplicate of the en-int version).
 */
export const buildEntityUrl = (type, slug, id, locale = null) => {
  const baseUrl = getBaseUrl();
  const pathMap = {
    profession: 'professions',
    article: 'articles',
    vacancy: 'vacancies',
    skill: 'skills',
    // Courses/platforms are mounted under /education/* (see routes/index.jsx).
    // Emitting bare /courses|/platforms produced canonicals to non-existent
    // routes (soft-404s).
    course: 'education/courses',
    platform: 'education/platforms',
    consultant: 'counselors',
    mentor: 'mentors',
    educationProgram: 'education-programs',
    company: 'company'
  };

  const entityPath = pathMap[type] || type;

  let localePath = '';
  if (locale) {
    localePath = `/${locale}-${LANG_TO_COUNTRY[locale]?.toLowerCase() || locale}`;
  } else if (typeof window !== 'undefined') {
    const m = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?:\/|$)/i);
    if (m) localePath = `/${m[1].toLowerCase()}`;
  }

  return `${baseUrl}${localePath}/${entityPath}/${encodeURIComponent(slug)}/${id}`;
};

/**
 * Build hreflang URLs for a given path.
 *
 * Note on slugs: every alternate inherits the path of the current page
 * (which means the current locale's slug). The frontend only has one locale's
 * profession/skill name at a time (each API request is stamped with
 * X-API-Language: <currentLocale>), so producing per-locale slugs would
 * require N parallel API fetches per page or a backend "all-locale names"
 * endpoint. For now we rely on each page's `<link rel="canonical">` —
 * computed from the local `profession.name` — to fold duplicates in Google's
 * index, plus the stale-slug client redirect on Profession/SkillDetail to
 * bounce inbound visits to the locale-correct URL.
 */
export const buildHreflangUrls = (path, supportedLocales) => {
  const urls = [];

  // Remove existing locale prefix from path if present (format: /{locale}-{country}/...)
  const cleanPath = path.replace(/^\/[a-z]{2}-[a-z]{2}/i, '') || '/';
  // Ensure path starts with /
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

  // The Ukrainian version always lives on profitroll.com.ua — hardcode it, not
  // SITE_CONFIG.url (which is the *current brand's* root, i.e. jobalto.com on a
  // jobalto build). Otherwise the uk-UA hreflang would point at jobalto.com.
  const profitrollBaseUrl = 'https://profitroll.com.ua';
  const jobaltoBaseUrl = `https://${JOBALTO_URL}`;

  // Always add Ukrainian version first (for profitroll.com.ua)
  urls.push({
    locale: 'uk-UA',
    url: `${profitrollBaseUrl}${normalizedPath}`
  });

  // Add all other supported locales (for jobalto.com)
  if (supportedLocales && Array.isArray(supportedLocales)) {
    supportedLocales.forEach((locale) => {
      // Skip Ukrainian locale as it's already added above
      if (PROFITROLL_URL_LANGUAGES.includes(locale)) {
        return;
      }

      const country = LANG_TO_COUNTRY[locale];
      if (!country) {
        return; // Skip if no country mapping
      }

      const localeTag = `${locale}-${country.toUpperCase()}`;
      const countryLower = country.toLowerCase();

      urls.push({
        locale: localeTag,
        url: `${jobaltoBaseUrl}/${locale}-${countryLower}${normalizedPath}`
      });
    });
  }

  // x-default → the neutral English entry point (jobalto en-gb). Ukrainian
  // (profitroll) is a specific locale, not a sensible default for unmatched
  // international visitors.
  urls.push({
    locale: 'x-default',
    url: `${jobaltoBaseUrl}/en-gb${normalizedPath}`
  });

  return urls;
};

/**
 * Build Open Graph URL
 */
export const buildOgUrl = (path) => {
  return buildCanonicalUrl(path);
};

/**
 * Clean and encode URL slug
 */
export const encodeSlug = (text) => {
  if (!text) return '';
  return encodeURIComponent(
    text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[<>:"/\\|?*]/g, '')
  );
};

/**
 * Build image URL with dimensions for Open Graph
 */
export const buildOgImageUrl = (imageUrl, width = 1200, height = 627) => {
  if (!imageUrl) return `${getBaseUrl()}${SITE_CONFIG.defaultImage}`;
  
  // If already has dimensions, return as is
  if (imageUrl.includes('/w') && imageUrl.includes('h')) {
    return imageUrl;
  }
  
  // Add dimensions suffix
  return `${imageUrl}/w${width}h${height}`;
};

/**
 * Build sitemap URL
 */
export const buildSitemapUrl = (path, lastmod, changefreq = 'weekly', priority = 0.5) => {
  return {
    url: buildCanonicalUrl(path),
    lastmod: lastmod || new Date().toISOString(),
    changefreq,
    priority
  };
};

/**
 * Get current path without query string
 */
export const getCurrentPath = () => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

/**
 * Build pagination URL
 */
export const buildPaginationUrl = (basePath, pageNumber) => {
  if (pageNumber <= 1) {
    return basePath;
  }
  return `${basePath}/page/${pageNumber}`;
};

/**
 * Get locale from URL path
 */
export const getLocaleFromPath = (path) => {
  const match = path.match(/^\/([a-z]{2})-([a-z]{2})\//i);
  if (match) {
    return {
      language: match[1].toLowerCase(),
      country: match[2].toUpperCase()
    };
  }
  return null;
};

/**
 * Remove locale prefix from path
 */
export const removeLocaleFromPath = (path) => {
  return path.replace(/^\/[a-z]{2}-[a-z]{2}/i, '') || '/';
};

const urlBuilder = {
  getBaseUrl,
  getProfessionSlug,
  getSkillSlug,
  getVacancySlug,
  buildCanonicalUrl,
  buildEntityUrl,
  buildHreflangUrls,
  buildOgUrl,
  encodeSlug,
  buildOgImageUrl,
  buildSitemapUrl,
  getCurrentPath,
  buildPaginationUrl,
  getLocaleFromPath,
  removeLocaleFromPath
};

export default urlBuilder;

